import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Box,
  Typography,
} from "@mui/material";
import {
  Layers,
  FolderOpen,
  Image as ImageIcon,
  PenTool,
  MessageSquare,
  Zap,
  Facebook,
  BookOpen,
  ExternalLink,
  MessageCircle,
  Lightbulb,
  LayoutGrid,
  Users,
  FolderGit2,
  Settings,
  Wand2,
} from "lucide-react";
import { Link as RouterLink, useLocation, Link } from "react-router-dom";
import { useLimits } from "../context/LimitsContext";
import SidenavCredits from "./SidenavCredits";
import logo from "../assets/mainLogo.png";
import character from "../assets/character.png";

const drawerWidth = 280;
const collapsedDrawerWidth = 70;
const HOVER_COLOR = '#9c27b0';

const CustomTooltip = ({ children, title, disabled }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const ref = useRef(null);

  useEffect(() => {
    if (isHovered && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPosition({
        top: rect.top + window.scrollY + rect.height / 2,
        left: rect.right + window.scrollX + 10,
      });
    }
  }, [isHovered]);

  if (!disabled) {
    return children;
  }

  return (
    <div
      ref={ref}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      {isHovered &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "absolute",
              top: `${position.top}px`,
              left: `${position.left}px`,
              transform: "translateY(-50%)",
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              color: "white",
              padding: "5px 10px",
              borderRadius: "4px",
              fontSize: "14px",
              whiteSpace: "nowrap",
              zIndex: 9999,
            }}
          >
            {title}
          </div>,
          document.body
        )}
    </div>
  );
};

function Sidenav({ open, onClose, isMobile }) {
  const theme = useTheme();
  const location = useLocation();
  const sidenavRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { limits } = useLimits();

  const menuItems = [
    {
      icon: <LayoutGrid size={20} strokeWidth={2} />,
      text: "Dashboard",
      path: "/",
    },
    {
      icon: <Wand2 size={20} strokeWidth={2} />,
      text: "Image Generation",
      path: "/images",
    },
    {
      icon: <FolderGit2 size={20} strokeWidth={2} />,
      text: "My Projects",
      path: "/projects",
    },
    {
      icon: <MessageCircle size={20} strokeWidth={2} />,
      text: "Chat History",
      path: "/chat",
    },
    {
      icon: <Settings size={20} strokeWidth={2} />,
      text: "My Settings",
      path: "/profile",
    },
  ];

  const handleMouseEnter = () => {
    if (!isMobile) {
      setIsExpanded(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsExpanded(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidenavRef.current && !sidenavRef.current.contains(event.target)) {
        if (isMobile) {
          onClose();
        } else {
          setIsExpanded(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobile, onClose]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 64); // 64px is the height of the TopBar
    };

    if (!isMobile) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [isMobile]);

  return (
    <>
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? open : true}
        onClose={onClose}
        ref={sidenavRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          width: isMobile
            ? drawerWidth
            : isExpanded
            ? drawerWidth
            : collapsedDrawerWidth,
          flexShrink: 0,
          zIndex: theme.zIndex.drawer + 2,
          [`& .MuiDrawer-paper`]: {
            width: isMobile
              ? drawerWidth
              : isExpanded
              ? drawerWidth
              : collapsedDrawerWidth,
            boxSizing: "border-box",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >
        {(isMobile || isExpanded) ? (
          <ListItem
            sx={{
              height: 64,
              borderBottom: `1px solid ${theme.palette.divider}`,
              px: 3,
            }}
          >
            {isMobile ? (
              <Link to="https://app.speare.ai" rel="noopener noreferrer">
                <img
                  src={logo}
                  alt="Logo"
                  style={{ 
                    height: "50px",
                    width: "auto", 
                    cursor: "pointer",
                  }}
                />
              </Link>
            ) : (
              <>
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: 3,
                    justifyContent: "center",
                    ml: "-10px",
                  }}
                >
                  <img
                    src={character}
                    alt="Character"
                    style={{ 
                      height: "40px",
                      width: "auto",
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="speare"
                  sx={{
                    ml: "-12px",
                    "& .MuiListItemText-primary": {
                      fontFamily: 'Poppins',
                      fontWeight: 700,
                      fontSize: '1.25rem',
                    }
                  }}
                />
              </>
            )}
          </ListItem>
        ) : (
          <ListItem
            sx={{
              height: 64,
              justifyContent: "center",
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            {!isMobile && (
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  justifyContent: "center",
                }}
              >
                <img
                  src={character}
                  alt="Character"
                  style={{ 
                    height: "30px",
                    width: "auto",
                  }}
                />
              </ListItemIcon>
            )}
          </ListItem>
        )}

        <Box sx={{ flexGrow: 1, mb: 2 }}>
          <List>
            {menuItems.map((item, index) => (
              <React.Fragment key={index}>
                <CustomTooltip title={item.tooltip} disabled={item.disabled}>
                  <ListItem
                    button
                    component={item.disabled ? 'div' : RouterLink}
                    to={!item.disabled ? item.path : undefined}
                    selected={
                      location.pathname === item.path ||
                      (item.path === '/meme-generator' && location.pathname.startsWith('/meme-generator/')) ||
                      (item.subItems &&
                        item.subItems.some(
                          (subItem) => location.pathname === subItem.path
                        ))
                    }
                    sx={{
                      justifyContent:
                        isExpanded || isMobile ? "flex-start" : "center",
                      px: isExpanded || isMobile ? 3 : 1,
                      opacity: item.disabled ? 0.5 : 1,
                      cursor: item.disabled ? 'not-allowed' : 'pointer',
                      "& .MuiTypography-root": {
                        fontSize: "0.875rem",
                      },
                      "&:hover": {
                        backgroundColor: "rgba(92, 1, 111, 0.04)",
                        "& .MuiListItemIcon-root, & .MuiTypography-root": {
                          color: HOVER_COLOR,
                        },
                      },
                      "&.Mui-selected": {
                        backgroundColor: "rgba(92, 1, 111, 0.08)",
                        "& .MuiListItemIcon-root, & .MuiTypography-root": {
                          color: HOVER_COLOR,
                        },
                        "&:hover": {
                          backgroundColor: "rgba(92, 1, 111, 0.12)",
                        },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: isExpanded || isMobile ? 3 : 0,
                        justifyContent: "center",
                        color: "text.secondary",
                        "& svg": {
                          width: 20,
                          height: 20,
                        },
                        transition: "color 0.2s",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {(isExpanded || isMobile) && (
                      <ListItemText
                        primary={item.text}
                        sx={{
                          color: "inherit",
                          opacity: isExpanded || isMobile ? 1 : 0,
                          transition: theme.transitions.create("opacity"),
                          "& .MuiListItemText-primary": {
                            fontSize: "0.875rem",
                          },
                        }}
                      />
                    )}
                  </ListItem>
                </CustomTooltip>

                {item.subItems && (isExpanded || isMobile) && (
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem, subIndex) => (
                      <ListItem
                        key={subIndex}
                        button
                        component={RouterLink}
                        to={subItem.path}
                        selected={location.pathname === subItem.path}
                        sx={{
                          pl: 7,
                          py: 1,
                          "&.Mui-selected": {
                            backgroundColor: "rgba(92, 1, 111, 0.08)",
                            "& .MuiListItemIcon-root, & .MuiTypography-root": {
                              color: HOVER_COLOR,
                            },
                            "&:hover": {
                              backgroundColor: "rgba(92, 1, 111, 0.12)",
                            },
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: 3,
                            justifyContent: "center",
                            color: "text.secondary",
                            "& svg": {
                              width: 20,
                              height: 20,
                            },
                            transition: "color 0.2s",
                          }}
                        >
                          {subItem.icon}
                        </ListItemIcon>
                        {(isExpanded || isMobile) && (
                          <ListItemText
                            primary={subItem.text}
                            sx={{
                              color: "inherit",
                              opacity: isExpanded || isMobile ? 1 : 0,
                              transition: theme.transitions.create("opacity"),
                              "& .MuiListItemText-primary": {
                                fontSize: "0.875rem",
                              },
                            }}
                          />
                        )}
                      </ListItem>
                    ))}
                  </List>
                )}
              </React.Fragment>
            ))}
          </List>
        </Box>
        <SidenavCredits isExpanded={isExpanded || isMobile} />
      </Drawer>
    </>
  );
}

export default Sidenav;
